import React, { useRef, useState } from "react";
import { bool } from "prop-types";
import { useDispatch } from "react-redux";
import { twMerge } from "tailwind-merge";

import Switch from "@components/shared/Switch";
import TextSwitch from "@components/shared/TextSwitch";
import CEEUnavailableModal from "@components/shared/CEEUnavailableModal";
import gtm from "@gtm/core";
import { useTranslation } from "@lib/i18n";

import {
  resetDeliveryData,
  setB2BLeasysAction,
  updateCarConfigurationAction,
  updateJourneyAction,
} from "@redux/actions/dealActions";
import { useIsB2BSelected } from "@hooks/useIsB2BSelected";
import { useIsCashJourneySelected } from "@hooks/useIsCashJourneySelected";
import { useIsLeasingJourneySelected } from "@hooks/useIsLeasingJourneySelected";
import { useIsJourneySwitchActive } from "@hooks/useIsJourneySwitchActive";

import {
  useSelectedJourneySelector,
  useActiveCarPreconfigurationSelector,
  useIsCEESubsidySelectedSelector,
  useBusinessModelSelector,
  useIsB2BLeasysSelected,
} from "@redux/reducers/deal";
import { changeFundingEvent } from "@gtm/events/shared";

import { getAvailableJourneys } from "@shared/helper/getAvailableJourneys";
import {
  useCEESubsidySelector,
  useConfigurationSelector,
} from "redux/reducers/appConfig";
import { useIsKeyAccount } from "@redux/reducers/auth";

import { withTabNavigation } from "@hoc/withTabNavigation";

import { CUSTOMIZE_TYPES, JOURNEY_TYPE } from "@shared/constants";
import { getCEESubsidy } from "@shared/v2/lib/getCEESubsidy";
import { getJourneyNameFromJourneyType } from "@shared/v2/lib/getJourneyNameFromJourneyType";

import api from "@api";

const SwitchLabel = withTabNavigation("label");

const JourneySwitch = ({
  isHorizontal,
  isTextVersion = false,
  isUIv2 = false,
}) => {
  const [
    isCEESubsidyUnavailableModalOpen,
    setIsCEESubsidyUnavailableModalOpen,
  ] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const isKeyAccount = useIsKeyAccount();

  const isB2BSelected = useIsB2BSelected();
  const isB2BLeasysModeAvailable = isKeyAccount && isB2BSelected;
  const isB2BLeasysSelected = useIsB2BLeasysSelected();

  const preconfiguration = useActiveCarPreconfigurationSelector();
  const isJourneySwitchActive = useIsJourneySwitchActive(preconfiguration);
  const isCashJourneySelected = useIsCashJourneySelected();
  const isLeasingJourneySelected =
    useIsLeasingJourneySelected() || isB2BLeasysSelected;
  const businessModel = useBusinessModelSelector();

  const selectedJourney = useSelectedJourneySelector();
  const configuration = useConfigurationSelector();
  const skipCEECheckRef = useRef(false);

  const ceeSubsidySelected = useIsCEESubsidySelectedSelector();
  const ceeSubsidy = useCEESubsidySelector(ceeSubsidySelected);

  const setJourneyType = nextJourney => {
    dispatch(updateJourneyAction(nextJourney));

    gtm.fire(changeFundingEvent(nextJourney));
  };

  const toggleJourneyType = () => {
    const availableJourneys = getAvailableJourneys(configuration);
    const nextJourneyType = isB2BLeasysSelected
      ? JOURNEY_TYPE.CASH
      : availableJourneys.find(journey => journey !== selectedJourney);

    if (isB2BLeasysModeAvailable) {
      const newLeasysState = nextJourneyType !== JOURNEY_TYPE.CASH;

      api.updateSession({ isB2BLeasysSelected: newLeasysState });
      dispatch(setB2BLeasysAction(newLeasysState));

      return;
    }

    if (isJourneySwitchActive) {
      if (
        !skipCEECheckRef.current &&
        ceeSubsidy?.enabled &&
        ceeSubsidySelected
      ) {
        const nextCEESubsidy = getCEESubsidy(
          getJourneyNameFromJourneyType(nextJourneyType),
          { configuration },
          businessModel,
          ceeSubsidySelected
        );

        if (!nextCEESubsidy?.enabled) {
          setIsCEESubsidyUnavailableModalOpen(true);
        }

        return;
      }

      skipCEECheckRef.current = false;

      dispatch(resetDeliveryData());
      dispatch(updateCarConfigurationAction({ [CUSTOMIZE_TYPES.SERVICE]: [] }));

      setJourneyType(nextJourneyType);
    }
  };

  const handleCEEUnavailableStay = () => {
    setIsCEESubsidyUnavailableModalOpen(false);
  };

  const handleCEEUnavailableContinue = () => {
    skipCEECheckRef.current = true;

    setIsCEESubsidyUnavailableModalOpen(false);

    toggleJourneyType();
  };

  const isCashOptionSelected = isB2BLeasysModeAvailable
    ? !isB2BLeasysSelected
    : isCashJourneySelected;

  const leasingTranslationKey = isB2BLeasysModeAvailable
    ? "price.leasys"
    : isUIv2
    ? "price.monthlyPayments"
    : "price.monthly";

  if (isTextVersion) {
    return (
      <>
        <CEEUnavailableModal
          isOpen={isCEESubsidyUnavailableModalOpen}
          dataId="cee-subsidy-unavailable-modal"
          onCancel={handleCEEUnavailableStay}
          onConfirm={handleCEEUnavailableContinue}
          ceeSubsidyType={ceeSubsidySelected}
        />
        <div
          className={twMerge(
            "flex items-center gap-8",
            "[&>*]:shrink-0",
            isJourneySwitchActive
              ? "opacity-100 pointer-events-auto"
              : "opacity-50 pointer-events-none"
          )}
          data-id="price-switcher-input-toggle"
        >
          <TextSwitch
            selectedText={t(leasingTranslationKey)}
            unselectedText={isUIv2 ? t("price.singlePayment") : t("price.cash")}
            checked={isCashOptionSelected}
            onChange={toggleJourneyType}
          />
        </div>
      </>
    );
  }

  if (isHorizontal) {
    return (
      <>
        <CEEUnavailableModal
          isOpen={isCEESubsidyUnavailableModalOpen}
          dataId="cee-subsidy-unavailable-modal"
          onCancel={handleCEEUnavailableStay}
          onConfirm={handleCEEUnavailableContinue}
          ceeSubsidyType={ceeSubsidySelected}
        />
        <div
          className={twMerge(
            "flex items-center gap-8",
            "[&>*]:shrink-0",
            isJourneySwitchActive
              ? "opacity-100 pointer-events-auto"
              : "opacity-50 pointer-events-none"
          )}
          data-id="price-switcher-input-toggle"
        >
          <SwitchLabel
            className={twMerge(
              "text-xs font-semibold transition duration-300 select-none cursor-pointer",
              isLeasingJourneySelected ? "text-white" : "text-white/50",
              isUIv2 && "smallMobile:whitespace-nowrap text-xs/6"
            )}
            onClick={toggleJourneyType}
          >
            {t(leasingTranslationKey)}
          </SwitchLabel>
          <Switch
            checked={isCashOptionSelected}
            onChange={toggleJourneyType}
            ariaLabel={
              isLeasingJourneySelected
                ? t(leasingTranslationKey)
                : isUIv2
                ? t("price.singlePayment")
                : t("price.cash")
            }
          />
          <SwitchLabel
            className={twMerge(
              "text-xs font-semibold transition duration-300 select-none cursor-pointer",
              isCashOptionSelected ? "text-white" : "text-white/50",
              isUIv2 && "smallMobile:whitespace-nowrap text-xs/6"
            )}
            onClick={toggleJourneyType}
          >
            {isUIv2 ? t("price.singlePayment") : t("price.cash")}
          </SwitchLabel>
        </div>
      </>
    );
  }

  return (
    <>
      <CEEUnavailableModal
        isOpen={isCEESubsidyUnavailableModalOpen}
        dataId="cee-subsidy-unavailable-modal"
        onCancel={handleCEEUnavailableStay}
        onConfirm={handleCEEUnavailableContinue}
        ceeSubsidyType={ceeSubsidySelected}
      />
      <div
        className={twMerge(
          "flex items-center h-full ml-1 py-2 smallMobile:ml-8 smallMobile:py-4 sm:ml-0 lg:ml-4 lg:mr-2",
          isJourneySwitchActive
            ? "opacity-100 pointer-events-auto"
            : "opacity-50 pointer-events-none",
          isUIv2 ? "lg:scale-110 lg:mx-0" : "lg:scale-130"
        )}
        data-id="price-switcher-input-toggle"
      >
        <Switch
          ariaLabel={
            isLeasingJourneySelected
              ? t(leasingTranslationKey)
              : isUIv2
              ? t("price.singlePayment")
              : t("price.cash")
          }
          checked={isCashOptionSelected}
          onChange={toggleJourneyType}
        />

        <div className="flex flex-col ml-2 lg:mr-2">
          <SwitchLabel
            className={twMerge(
              "text-xs font-semibold transition duration-300 select-none cursor-pointer first:mb-2",
              isLeasingJourneySelected ? "text-white" : "text-white/50",
              isUIv2 && "smallMobile:whitespace-nowrap text-xs/6"
            )}
            onClick={toggleJourneyType}
          >
            {t(leasingTranslationKey)}
          </SwitchLabel>
          <SwitchLabel
            className={twMerge(
              "text-xs font-semibold transition duration-300 select-none cursor-pointer first:mb-2",
              isCashOptionSelected ? "text-white" : "text-white/50",
              isUIv2 && "smallMobile:whitespace-nowrap text-xs/6"
            )}
            onClick={toggleJourneyType}
          >
            {isUIv2 ? t("price.singlePayment") : t("price.cash")}
          </SwitchLabel>
        </div>
      </div>
    </>
  );
};

JourneySwitch.propTypes = {
  isHorizontal: bool,
  isTextVersion: bool,
  isUIv2: bool,
};

export default JourneySwitch;
